/**
 * Warm create/edit flow for Priceless Possessions (keepsakes / memory objects).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Gem, ImagePlus, Lock, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import SmartDateInput from '../SmartDateInput';
import { toast } from 'sonner';
import { humanizeError } from '../../utils/errorHandling';
import {
  CUSTOMER_IMAGE_ACCEPT,
  CUSTOMER_IMAGE_FORMATS_LINE,
  IMAGE_PREVIEW_UNSUPPORTED_HINT,
  validateCustomerImageFile,
  normalizeImageUploadErrorDetail,
} from '../../utils/imageUploadValidation';
import {
  POSSESSION_CATEGORY_LABELS,
  POSSESSION_PRIVACY_LABELS,
  buildPersonAttachment,
  createMemoryMarker,
  deleteMemoryMarkerImage,
  getMarkerDisplayImage,
  possessionEmptyPayload,
  updateMemoryMarker,
  uploadMemoryMarkerImage,
} from '../../utils/memoryMarkers';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getApiBase } from '../../utils/apiBase';
import { getDisplayName, isGenealogicalPerson } from '../../utils/companionProfiles';

const BACKEND_URL = getApiBase().replace(/\/api$/, '');

function personLabel(p) {
  return p ? getDisplayName(p) : 'Person';
}

export function PricelessPossessionDialog({
  open,
  onOpenChange,
  treeId,
  marker = null,
  defaultPersonId = null,
  genealogicalPeople = [],
  stories = [],
  onSaved,
}) {
  const isEdit = Boolean(marker?.id);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => possessionEmptyPayload({ personId: defaultPersonId }));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewFailed, setImagePreviewFailed] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const fileInputRef = useRef(null);

  const people = useMemo(
    () => (genealogicalPeople || []).filter((p) => isGenealogicalPerson(p)),
    [genealogicalPeople],
  );

  useEffect(() => {
    if (!open) return;
    setImageFile(null);
    setRemoveImage(false);
    if (marker) {
      setForm({
        marker_type: marker.marker_type === 'object' ? marker.marker_type : 'keepsake',
        title: marker.title || '',
        description: marker.description || '',
        why_it_matters: marker.why_it_matters || '',
        story_text: marker.story_text || '',
        category: marker.category || 'other',
        approx_date: marker.approx_date || '',
        location_label: marker.location_label || '',
        attachment: marker.attachment || buildPersonAttachment(defaultPersonId),
        connected_person_ids: marker.connected_person_ids?.length
          ? marker.connected_person_ids
          : marker.attachment?.person_id
            ? [marker.attachment.person_id]
            : [],
        primary_person_id: marker.primary_person_id || marker.attachment?.person_id || defaultPersonId,
        linked_story_id: marker.linked_story_id || null,
        voice_note_id: marker.voice_note_id || null,
        privacy: marker.privacy || 'private',
        include_in_pdf: marker.include_in_pdf !== false,
        include_in_memorial: Boolean(marker.include_in_memorial),
        tags: marker.tags || [],
      });
      const existing = getMarkerDisplayImage(marker);
      setImagePreview(existing ? resolveAssetUrl(BACKEND_URL, existing) : null);
    } else {
      setForm(possessionEmptyPayload({ personId: defaultPersonId }));
      setImagePreview(null);
    }
  }, [open, marker, defaultPersonId]);

  const toggleConnectedPerson = (personId) => {
    setForm((prev) => {
      const ids = prev.connected_person_ids || [];
      const next = ids.includes(personId) ? ids.filter((x) => x !== personId) : [...ids, personId];
      const primary = prev.primary_person_id || personId;
      return {
        ...prev,
        connected_person_ids: next,
        primary_person_id: next.includes(primary) ? primary : next[0] || null,
        attachment: next[0] ? buildPersonAttachment(next[0]) : prev.attachment,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const title = (form.title || '').trim();
    if (!title) {
      toast.error('Please name this possession.');
      return;
    }
    if (!treeId) return;

    const primaryId = form.primary_person_id || form.connected_person_ids?.[0] || defaultPersonId;
    const connected = form.connected_person_ids?.length
      ? form.connected_person_ids
      : primaryId
        ? [primaryId]
        : [];

    const payload = {
      marker_type: form.marker_type || 'keepsake',
      title,
      description: (form.description || '').trim() || null,
      why_it_matters: (form.why_it_matters || '').trim() || null,
      story_text: (form.story_text || '').trim() || null,
      category: form.category || 'other',
      approx_date: (form.approx_date || '').trim() || null,
      location_label: (form.location_label || '').trim() || null,
      attachment: primaryId ? buildPersonAttachment(primaryId) : form.attachment,
      connected_person_ids: connected,
      primary_person_id: primaryId || null,
      linked_story_id: form.linked_story_id || null,
      voice_note_id: form.voice_note_id || null,
      privacy: form.privacy || 'private',
      include_in_pdf: Boolean(form.include_in_pdf),
      include_in_memorial: Boolean(form.include_in_memorial),
      tags: [],
    };

    setSaving(true);
    try {
      const saved = isEdit
        ? await updateMemoryMarker(marker.id, payload)
        : await createMemoryMarker(treeId, payload);
      const markerId = saved?.id || marker?.id;
      let imageWarning = null;
      if (markerId && imageFile) {
        try {
          await uploadMemoryMarkerImage(markerId, imageFile);
        } catch (imgErr) {
          imageWarning = normalizeImageUploadErrorDetail(humanizeError(imgErr));
        }
      } else if (markerId && removeImage && isEdit) {
        await deleteMemoryMarkerImage(markerId);
      }
      toast.success(isEdit ? 'Keepsake updated.' : 'This memory has been added to the archive.');
      if (imageWarning) {
        toast.warning('Keepsake saved, but the photo could not be uploaded.', {
          description: imageWarning,
        });
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      toast.error(humanizeError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = validateCustomerImageFile(file);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    setImageFile(file);
    setRemoveImage(false);
    setImagePreviewFailed(false);
    setImagePreview(URL.createObjectURL(file));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] sm:max-w-lg" data-testid="priceless-possession-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">
            {isEdit ? 'Edit priceless possession' : 'Add a priceless possession'}
          </DialogTitle>
          <DialogDescription className="text-[0.95rem] leading-relaxed text-[#5c534a]">
            Every object has a story. Preserve the reason it mattered — not every memory is a portrait.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="pp-title">Name this possession</Label>
            <Input
              id="pp-title"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Grandad's watch, Mum's recipe book"
              required
              className="border-[hsl(38_14%_88%)] font-serif"
            />
          </div>

          <div className="space-y-2">
            <Label>Upload photo</Label>
            {imagePreview ? (
              <div className="relative inline-block">
                {imagePreviewFailed ? (
                  <p className="max-w-[14rem] text-xs text-muted-foreground">{IMAGE_PREVIEW_UNSUPPORTED_HINT}</p>
                ) : (
                  <img
                    src={imagePreview}
                    alt=""
                    className="h-32 w-32 rounded-xl border object-cover shadow-sm"
                    onError={() => setImagePreviewFailed(true)}
                  />
                )}
                <Button type="button" size="icon" variant="secondary" className="absolute -right-2 -top-2 h-7 w-7 rounded-full" onClick={() => { setImageFile(null); setRemoveImage(true); setImagePreview(null); setImagePreviewFailed(false); }}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="rounded-full" onClick={() => fileInputRef.current?.click()}>
                <ImagePlus className="mr-2 h-4 w-4" /> Add photo
              </Button>
            )}
            <input ref={fileInputRef} type="file" accept={CUSTOMER_IMAGE_ACCEPT} className="hidden" onChange={handleImagePick} />
          </div>

          <div className="space-y-2">
            <Label>What is it?</Label>
            <Select value={form.category || 'other'} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
              <SelectTrigger className="border-[hsl(38_14%_88%)] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(POSSESSION_CATEGORY_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {people.length > 0 && (
            <div className="space-y-2 rounded-xl border border-[hsl(38_14%_88%)]/80 bg-background/40 p-3">
              <Label>Who is connected to it?</Label>
              <p className="text-[11px] text-muted-foreground">Select everyone this keepsake belongs to or reminds you of.</p>
              <div className="flex flex-wrap gap-2">
                {people.map((p) => {
                  const selected = (form.connected_person_ids || []).includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleConnectedPerson(p.id)}
                      className={`rounded-full border px-3 py-1 text-xs transition ${
                        selected ? 'border-[#8B7355]/50 bg-[#8B7355]/10 text-[#1c1917]' : 'border-[hsl(38_14%_88%)] text-muted-foreground'
                      }`}
                    >
                      {personLabel(p)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="pp-why">Why does it matter?</Label>
            <Textarea
              id="pp-why"
              value={form.why_it_matters}
              onChange={(e) => setForm((p) => ({ ...p, why_it_matters: e.target.value }))}
              placeholder="What memory comes back when you see it?"
              rows={2}
              className="resize-none border-[hsl(38_14%_88%)]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pp-story">Tell the story behind it</Label>
            <Textarea
              id="pp-story"
              value={form.story_text}
              onChange={(e) => setForm((p) => ({ ...p, story_text: e.target.value }))}
              placeholder="Is there a story future generations should know?"
              rows={3}
              className="resize-none border-[hsl(38_14%_88%)] font-serif text-sm leading-relaxed"
            />
          </div>

          <SmartDateInput mode="simple" label="Date or era (optional)" value={form.approx_date} onChange={(v) => setForm((p) => ({ ...p, approx_date: v || '' }))} />

          <div className="space-y-2">
            <Label htmlFor="pp-loc">Where was it kept? (optional)</Label>
            <Input id="pp-loc" value={form.location_label} onChange={(e) => setForm((p) => ({ ...p, location_label: e.target.value }))} placeholder="e.g. Top drawer in the front room" className="border-[hsl(38_14%_88%)]" />
          </div>

          {stories.length > 0 && (
            <div className="space-y-2">
              <Label>Link a voice memory (optional)</Label>
              <Select
                value={form.voice_note_id || '__none__'}
                onValueChange={(v) => setForm((p) => ({ ...p, voice_note_id: v === '__none__' ? null : v }))}
              >
                <SelectTrigger className="border-[hsl(38_14%_88%)] bg-white"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {stories.filter((s) => s.audio_file_url || s.audio_segments?.length).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{(s.display_title || 'Voice memory').slice(0, 50)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Privacy</Label>
            <Select value={form.privacy || 'private'} onValueChange={(v) => setForm((p) => ({ ...p, privacy: v }))}>
              <SelectTrigger className="border-[hsl(38_14%_88%)] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(POSSESSION_PRIVACY_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 rounded-xl border border-[hsl(38_14%_88%)]/60 bg-muted/10 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Include in Legacy Book PDF</p>
                <p className="text-[11px] text-muted-foreground">Only meaningful keepsakes with context are exported.</p>
              </div>
              <Switch checked={form.include_in_pdf} onCheckedChange={(v) => setForm((p) => ({ ...p, include_in_pdf: v }))} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Include on memorial page</p>
                <p className="text-[11px] text-muted-foreground">Only when a memorial is published for this person.</p>
              </div>
              <Switch checked={form.include_in_memorial} onCheckedChange={(v) => setForm((p) => ({ ...p, include_in_memorial: v }))} />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.title.trim()} className="rounded-full gap-1.5" data-testid="priceless-possession-save">
              <Gem className="h-3.5 w-3.5" />
              {saving ? 'Saving…' : isEdit ? 'Save keepsake' : 'Preserve keepsake'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PricelessPossessionDialog;
